/**
 * Companion configuration editor.
 *
 * Fields are rendered from the config the API returns (plus the metadata it
 * ships: known OS types, feature keys, section names), so adding a config key
 * on the server surfaces here without a UI change.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle, Lock, Save } from 'lucide-react';

type ConfigValue = any;
export type CompanionConfig = Record<string, Record<string, ConfigValue>>;

export interface CompanionSettingsPayload {
  config: CompanionConfig;
  platform_allowed?: boolean;
  locked_sections?: string[];
  updated_by?: string | null;
  updated_at?: string | null;
  known_os_types?: string[];
  feature_keys?: string[];
  config_sections?: string[];
}

interface Props {
  payload: CompanionSettingsPayload | null;
  loading: boolean;
  saving: boolean;
  /** Platform admins bypass locked sections. */
  canEditLocked?: boolean;
  onSave: (patch: CompanionConfig) => Promise<void>;
  /** Rendered under the header (used for the Super Admin lock controls). */
  headerExtra?: React.ReactNode;
}

const SECTION_LABELS: Record<string, string> = {
  enablement: 'Enablement',
  features: 'Features',
  branding: 'Branding',
  session: 'Sessions & tokens',
  notifications: 'Notifications',
  support: 'Support tickets',
  versions: 'Versions',
  ai: 'AI assistant',
};

const SECTION_HINTS: Record<string, string> = {
  enablement: 'Turn the companion on and choose which operating systems and install modes are served.',
  features: 'Each toggle hides the matching section in the companion and blocks its API.',
  branding: 'Text and colours the companion shows. Use {name} in the greeting for the user name.',
  session: 'Token lifetime and how many concurrent sessions one install may hold.',
  notifications: 'Allowed severities and categories admins can send, plus retention.',
  support: 'Ticket categories, priorities, numbering prefix and per-user open limits.',
  versions: 'Minimum supported build and download links used to flag outdated installs.',
  ai: 'Ask Buntu. While disabled the companion shows the placeholder reply below.',
};

function label(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function isStringList(value: ConfigValue): boolean {
  return Array.isArray(value) && value.every((v) => typeof v === 'string' || typeof v === 'number');
}

const CompanionSettingsForm: React.FC<Props> = ({
  payload,
  loading,
  saving,
  canEditLocked = false,
  onSave,
  headerExtra,
}) => {
  const [draft, setDraft] = useState<CompanionConfig>({});
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (payload?.config) setDraft(JSON.parse(JSON.stringify(payload.config)));
  }, [payload]);

  const lockedSections = useMemo(
    () => new Set(canEditLocked ? [] : payload?.locked_sections || []),
    [payload, canEditLocked]
  );

  const sections = useMemo(() => {
    const known = payload?.config_sections || [];
    const present = Object.keys(draft).filter((k) => draft[k] && typeof draft[k] === 'object');
    const ordered = known.filter((k) => present.includes(k));
    return [...ordered, ...present.filter((k) => !ordered.includes(k))];
  }, [draft, payload]);

  const setValue = (section: string, key: string, value: ConfigValue) => {
    setSaved(false);
    setDraft((prev) => ({ ...prev, [section]: { ...(prev[section] || {}), [key]: value } }));
  };

  const handleSave = async () => {
    setError(null);
    setSaved(false);
    const patch: CompanionConfig = {};
    sections.forEach((section) => {
      if (!lockedSections.has(section)) patch[section] = draft[section];
    });
    try {
      await onSave(patch);
      setSaved(true);
    } catch (e: any) {
      setError(e?.message || 'Could not save companion settings');
    }
  };

  if (loading) {
    return <div className="py-10 text-center text-sm text-slate-500">Loading companion configuration…</div>;
  }
  if (!payload) {
    return <div className="py-10 text-center text-sm text-slate-500">No companion configuration available.</div>;
  }

  const renderField = (section: string, key: string, value: ConfigValue) => {
    const disabled = lockedSections.has(section);
    const fieldId = `${section}.${key}`;

    if (typeof value === 'boolean') {
      return (
        <label key={fieldId} className="flex items-center gap-3 py-1.5">
          <input
            type="checkbox"
            checked={value}
            disabled={disabled}
            onChange={(e) => setValue(section, key, e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-slate-700">{label(key)}</span>
        </label>
      );
    }

    if (typeof value === 'number') {
      return (
        <div key={fieldId} className="py-1.5">
          <label className="block text-xs font-medium text-slate-500 mb-1">{label(key)}</label>
          <input
            type="number"
            value={value}
            disabled={disabled}
            onChange={(e) => setValue(section, key, Number(e.target.value))}
            className="w-40 rounded border border-slate-300 px-3 py-1.5 text-sm disabled:bg-slate-100"
          />
        </div>
      );
    }

    if (isStringList(value)) {
      const options =
        key === 'supported_os'
          ? payload.known_os_types || []
          : null;
      if (options && options.length) {
        const selected = new Set((value as string[]).map(String));
        return (
          <div key={fieldId} className="py-1.5">
            <label className="block text-xs font-medium text-slate-500 mb-1">{label(key)}</label>
            <div className="flex flex-wrap gap-3">
              {options.map((option) => (
                <label key={option} className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={selected.has(option)}
                    disabled={disabled}
                    onChange={(e) => {
                      const next = new Set(selected);
                      if (e.target.checked) next.add(option);
                      else next.delete(option);
                      setValue(section, key, options.filter((o) => next.has(o)));
                    }}
                    className="h-4 w-4 rounded border-slate-300 text-blue-600"
                  />
                  {option}
                </label>
              ))}
            </div>
          </div>
        );
      }
      return (
        <div key={fieldId} className="py-1.5">
          <label className="block text-xs font-medium text-slate-500 mb-1">
            {label(key)} <span className="text-slate-400">(comma separated)</span>
          </label>
          <input
            type="text"
            value={(value as string[]).join(', ')}
            disabled={disabled}
            onChange={(e) =>
              setValue(
                section,
                key,
                e.target.value
                  .split(',')
                  .map((v) => v.trim())
                  .filter(Boolean)
              )
            }
            className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm disabled:bg-slate-100"
          />
        </div>
      );
    }

    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return (
        <div key={fieldId} className="py-1.5">
          <label className="block text-xs font-medium text-slate-500 mb-1">{label(key)}</label>
          <div className="space-y-2 rounded border border-slate-200 bg-slate-50 p-3">
            {Object.entries(value).map(([subKey, subValue]) => (
              <div key={subKey}>
                <label className="block text-xs text-slate-500 mb-1">{label(subKey)}</label>
                <input
                  type="text"
                  value={String(subValue ?? '')}
                  disabled={disabled}
                  onChange={(e) => setValue(section, key, { ...value, [subKey]: e.target.value })}
                  className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm disabled:bg-slate-100"
                />
              </div>
            ))}
          </div>
        </div>
      );
    }

    const isColor = typeof value === 'string' && /^#[0-9a-fA-F]{3,8}$/.test(value);
    const isLong = typeof value === 'string' && value.length > 60;
    return (
      <div key={fieldId} className="py-1.5">
        <label className="block text-xs font-medium text-slate-500 mb-1">{label(key)}</label>
        {isLong ? (
          <textarea
            value={String(value ?? '')}
            disabled={disabled}
            rows={3}
            onChange={(e) => setValue(section, key, e.target.value)}
            className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm disabled:bg-slate-100"
          />
        ) : (
          <div className="flex items-center gap-2">
            <input
              type={isColor ? 'color' : 'text'}
              value={String(value ?? '')}
              disabled={disabled}
              onChange={(e) => setValue(section, key, e.target.value)}
              className={`${isColor ? 'h-9 w-14 p-1' : 'w-full'} rounded border border-slate-300 px-3 py-1.5 text-sm disabled:bg-slate-100`}
            />
            {isColor && <span className="text-sm text-slate-600">{String(value)}</span>}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm text-slate-600">
            All companion behaviour comes from this configuration. Nothing is hard-coded in the app.
          </p>
          {payload.updated_at && (
            <p className="text-xs text-slate-400 mt-1">
              Last saved {new Date(payload.updated_at).toLocaleString()}
              {payload.updated_by ? ` by ${payload.updated_by}` : ''}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
        >
          <Save size={16} />
          {saving ? 'Saving…' : 'Save configuration'}
        </button>
      </div>

      {headerExtra}

      {payload.platform_allowed === false && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <Lock size={16} className="mt-0.5" />
          The platform administrator has withheld Buntu Companion from this tenant, so it stays disabled.
        </div>
      )}
      {error && (
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle size={16} className="mt-0.5" />
          {error}
        </div>
      )}
      {saved && (
        <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
          <CheckCircle size={16} className="mt-0.5" />
          Configuration saved.
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {sections.map((section) => {
          const locked = lockedSections.has(section);
          return (
            <section key={section} className="rounded-lg border border-slate-200 bg-white p-4">
              <header className="mb-3 flex items-start justify-between gap-2">
                <div>
                  <h4 className="text-sm font-semibold text-slate-800">
                    {SECTION_LABELS[section] || label(section)}
                  </h4>
                  {SECTION_HINTS[section] && (
                    <p className="mt-0.5 text-xs text-slate-500">{SECTION_HINTS[section]}</p>
                  )}
                </div>
                {locked && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                    <Lock size={12} /> Locked by platform
                  </span>
                )}
              </header>
              <div className="space-y-1">
                {Object.entries(draft[section] || {}).map(([key, value]) => renderField(section, key, value))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
};

export default CompanionSettingsForm;
